import React, { useRef, useState } from "react";
import {
  Box,
  Flex,
  Select,
  Button,
  Text,
  Icon,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
} from "@chakra-ui/react";
import {
  MdFormatAlignLeft,
  MdFormatAlignCenter,
  MdFormatAlignRight,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatColorFill,
  MdFormatColorText,
} from "react-icons/md";

// Define color palettes for text and highlight colors.
const textColors = ["#ff0000", "#0000ff", "#00ff00", "#000000", "#ffa500"];
const highlightColors = ["#ffff00", "#add8e6", "#90ee90", "#ffb6c1", "#d3d3d3"];

// Define default colors (must be one of the options above)
const defaultTextColor = "#ff0000";
const defaultHighlightColor = "#ffff00";

const RichTextEditor: React.FC = () => {
  // Create a ref for the contentEditable area.
  const editorRef = useRef<HTMLDivElement>(null);

  // State to hold the currently selected colors.
  const [currentTextColor, setCurrentTextColor] =
    useState<string>(defaultTextColor);
  const [currentHighlightColor, setCurrentHighlightColor] = useState<string>(
    defaultHighlightColor
  );

  // A helper function to execute formatting commands and then refocus the editor.
  const handleCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  // For highlight: use 'hiliteColor' if supported; else fallback to 'backColor'
  const handleHighlight = (color: string) => {
    if (document.queryCommandSupported("hiliteColor")) {
      handleCommand("hiliteColor", color);
    } else {
      handleCommand("backColor", color);
    }
  };

  return (
    <>
      {/* Toolbar */}
      <Flex
        mb={4}
        align="center"
        gap={2}
        flexWrap="wrap"
        border="1px solid #ccc"
        p={2}
      >
        {/* Font Family */}
        <Select
          w="150px"
          defaultValue="Arial"
          size="sm"
          onChange={(e) => handleCommand("fontName", e.target.value)}
        >
          <option value="Arial">Arial</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Courier New">Courier New</option>
        </Select>

        {/* Font Size */}
        <Select
          w="100px"
          defaultValue="3"
          size="sm"
          onChange={(e) => handleCommand("fontSize", e.target.value)}
        >
          <option value="1">small</option>
          <option value="3">medium</option>
          <option value="5">large</option>
        </Select>

        {/* Bold */}
        <Tooltip label="Bold (Ctrl + B)" hasArrow>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={() => handleCommand("bold")}
          >
            <Text as="b" fontSize="md">
              B
            </Text>
          </Button>
        </Tooltip>

        {/* Italic */}
        <Tooltip label="Italic (Ctrl + I)" hasArrow>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={() => handleCommand("italic")}
          >
            <Text as="i" fontSize="md">
              I
            </Text>
          </Button>
        </Tooltip>

        {/* Underline */}
        <Tooltip label="Underline (Ctrl + U)" hasArrow>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={() => handleCommand("underline")}
          >
            <Text as="u" fontSize="md">
              U
            </Text>
          </Button>
        </Tooltip>

        {/* Text Color Popover with Toggle */}
        <Popover placement="bottom-start">
          <PopoverTrigger>
            <Tooltip label="Text Color" hasArrow>
              <Button size="sm" variant="outline" colorScheme="gray">
                <Icon
                  as={MdFormatColorText as React.ElementType}
                  boxSize={5}
                  color={currentTextColor}
                />
              </Button>
            </Tooltip>
          </PopoverTrigger>
          <PopoverContent w="fit-content">
            <PopoverArrow />
            <PopoverBody>
              <Flex gap={2}>
                {textColors.map((color) => (
                  <Box
                    key={color}
                    w="20px"
                    h="20px"
                    bg={color}
                    border="1px solid #ccc"
                    borderRadius="sm"
                    cursor="pointer"
                    onClick={() => {
                      // Toggle: if the same color is selected, revert to default text color
                      if (currentTextColor === color) {
                        setCurrentTextColor(defaultTextColor);
                        handleCommand("foreColor", defaultTextColor);
                      } else {
                        setCurrentTextColor(color);
                        handleCommand("foreColor", color);
                      }
                    }}
                  />
                ))}
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Popover>

        {/* Highlight Color Popover with Toggle */}
        <Popover placement="bottom-start">
          <PopoverTrigger>
            <Tooltip label="Highlight Color" hasArrow>
              <Button size="sm" variant="outline" colorScheme="gray">
                <Icon
                  as={MdFormatColorFill as React.ElementType}
                  boxSize={5}
                  color={currentHighlightColor}
                />
              </Button>
            </Tooltip>
          </PopoverTrigger>
          <PopoverContent w="fit-content">
            <PopoverArrow />
            <PopoverBody>
              <Flex gap={2}>
                {highlightColors.map((color) => (
                  <Box
                    key={color}
                    w="20px"
                    h="20px"
                    bg={color}
                    border="1px solid #ccc"
                    borderRadius="sm"
                    cursor="pointer"
                    onClick={() => {
                      // Toggle: if the same color is selected, revert to default highlight color
                      if (currentHighlightColor === color) {
                        setCurrentHighlightColor(defaultHighlightColor);
                        handleHighlight(defaultHighlightColor);
                      } else {
                        setCurrentHighlightColor(color);
                        handleHighlight(color);
                      }
                    }}
                  />
                ))}
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Popover>

        {/* Align Left */}
        <Tooltip label="Align Left" hasArrow>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={() => handleCommand("justifyLeft")}
          >
            <Icon as={MdFormatAlignLeft as React.ElementType} boxSize={5} />
          </Button>
        </Tooltip>

        {/* Align Center */}
        <Tooltip label="Align Center" hasArrow>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={() => handleCommand("justifyCenter")}
          >
            <Icon as={MdFormatAlignCenter as React.ElementType} boxSize={5} />
          </Button>
        </Tooltip>

        {/* Align Right */}
        <Tooltip label="Align Right" hasArrow>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={() => handleCommand("justifyRight")}
          >
            <Icon as={MdFormatAlignRight as React.ElementType} boxSize={5} />
          </Button>
        </Tooltip>

        {/* Bulleted List */}
        <Tooltip label="Bulleted List" hasArrow>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={() => handleCommand("insertUnorderedList")}
          >
            <Icon as={MdFormatListBulleted as React.ElementType} boxSize={5} />
          </Button>
        </Tooltip>

        {/* Numbered List */}
        <Tooltip label="Numbered List" hasArrow>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={() => handleCommand("insertOrderedList")}
          >
            <Icon as={MdFormatListNumbered as React.ElementType} boxSize={5} />
          </Button>
        </Tooltip>
      </Flex>

      {/* Editable Content Area */}
      <Box
        border="1px solid #ccc"
        p={4}
        minH="150px"
        contentEditable
        suppressContentEditableWarning={true}
        ref={editorRef}
      >
        {/* For best results, select text or place the caret here before applying color changes. */}
      </Box>
    </>
  );
};

export default RichTextEditor;
